import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Star, ArrowLeft, Phone, User, MessageSquare, 
  CheckCircle, Clock, AlertCircle, Filter, Mail, ThumbsDown, ThumbsUp, Shield
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: 'New', icon: AlertCircle, color: 'bg-red-100 text-red-700' },
  contacted: { label: 'Contacted', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
  resolved: { label: 'Resolved', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  positive: { label: 'Positive', icon: ThumbsUp, color: 'bg-blue-100 text-blue-700' }
};

const FeedbackManagementPage = () => {
  const { user, isImpersonating } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${API}/plaques/feedback/negative`);
      setFeedback(response.data);
    } catch (error) {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (ratingId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/plaques/feedback/${ratingId}/status`, {
        status: newStatus,
        notes: notes
      });
      toast.success('Status updated');
      fetchFeedback();
      setSelectedFeedback(null);
      setNotes('');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const filteredFeedback = feedback.filter(f => {
    if (filterStatus !== 'all' && f.status !== filterStatus) return false;
    if (filterType === 'negative' && !f.is_negative) return false;
    if (filterType === 'declined' && f.is_negative) return false;
    return true;
  });

  const stats = {
    total: feedback.length,
    negative: feedback.filter(f => f.is_negative).length,
    declined: feedback.filter(f => !f.is_negative).length,
    new: feedback.filter(f => f.status === 'new').length,
    contacted: feedback.filter(f => f.status === 'contacted').length,
    resolved: feedback.filter(f => f.status === 'resolved').length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)] py-8 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[var(--color-text-secondary)] hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
          {user?.is_admin && !isImpersonating && (
            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors"
              data-testid="admin-dashboard-link"
            >
              <Shield className="w-4 h-4" />
              Admin Dashboard
            </Link>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-dashboard-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Feedback Management
            </h1>
            <p className="text-[var(--color-text-secondary)] mt-1">
              Review and respond to customer feedback
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          <Card className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filterType === 'all' && filterStatus === 'all' ? 'ring-2 ring-gray-400' : ''}`} onClick={() => { setFilterType('all'); setFilterStatus('all'); }}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold">{stats.total}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Total</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filterType === 'negative' ? 'ring-2 ring-red-500' : ''}`} onClick={() => { setFilterType('negative'); setFilterStatus('all'); }}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold text-red-600">{stats.negative}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Negative</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filterType === 'declined' ? 'ring-2 ring-blue-500' : ''}`} onClick={() => { setFilterType('declined'); setFilterStatus('all'); }}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold text-blue-600">{stats.declined}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Declined Review</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filterStatus === 'new' ? 'ring-2 ring-orange-500' : ''}`} onClick={() => { setFilterType('all'); setFilterStatus('new'); }}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold text-orange-600">{stats.new}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">New</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filterStatus === 'resolved' ? 'ring-2 ring-green-500' : ''}`} onClick={() => { setFilterType('all'); setFilterStatus('resolved'); }}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold text-green-600">{stats.resolved}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Resolved</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4 mb-6">
          <Filter className="w-4 h-4 text-[var(--color-text-muted)]" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Feedback</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Feedback List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="font-dashboard-heading text-lg font-semibold">
              Customer Feedback ({filteredFeedback.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredFeedback.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-dashboard-heading font-semibold text-lg mb-2">
                  {filterStatus === 'all' ? 'No feedback yet' : `No ${filterStatus} feedback`}
                </h3>
                <p className="text-[var(--color-text-muted)]">
                  Feedback will appear here when customers leave ratings below your threshold
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredFeedback.map((item) => {
                  const StatusIcon = statusConfig[item.status]?.icon || AlertCircle;
                  return (
                    <div 
                      key={item.id} 
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedFeedback(item)}
                      data-testid={`feedback-item-${item.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= item.stars 
                                      ? 'fill-yellow-400 text-yellow-400' 
                                      : 'text-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                            <Badge className={statusConfig[item.status]?.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig[item.status]?.label}
                            </Badge>
                            {!item.is_negative && (
                              <Badge className="bg-blue-50 text-blue-600">Declined Review</Badge>
                            )}
                            <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                              {item.plaque_code}
                            </code>
                          </div>
                          
                          {item.feedback && (
                            <p className="text-[var(--color-text-secondary)] mb-2 line-clamp-2">
                              {item.feedback}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap gap-4 text-sm text-[var(--color-text-muted)]">
                            {item.customer_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {item.customer_name}
                              </span>
                            )}
                            {item.customer_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {item.customer_email}
                              </span>
                            )}
                            {item.customer_mobile && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {item.customer_mobile}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Feedback Details</DialogTitle>
            </DialogHeader>
            {selectedFeedback && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`w-5 h-5 ${
                          star <= selectedFeedback.stars 
                            ? 'fill-yellow-400 text-yellow-400' 
                            : 'text-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-lg font-semibold">{selectedFeedback.stars} stars</span>
                  {!selectedFeedback.is_negative && (
                    <Badge className="bg-blue-50 text-blue-600 ml-2">Declined Review</Badge>
                  )}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-[var(--color-text-muted)] mb-1">Feedback</p>
                  <p>{selectedFeedback.feedback || 'No feedback provided'}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-muted)] mb-1">Customer Name</p>
                    <p>{selectedFeedback.customer_name || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-muted)] mb-1">Email</p>
                    <p>{selectedFeedback.customer_email || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-muted)] mb-1">Mobile</p>
                    <p>{selectedFeedback.customer_mobile || 'Not provided'}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-muted)] mb-1">Plaque Code</p>
                  <code className="bg-gray-100 px-2 py-1 rounded">{selectedFeedback.plaque_code}</code>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-muted)] mb-2">Update Status</p>
                  <div className="flex gap-2 mb-3">
                    {Object.entries(statusConfig).map(([status, config]) => (
                      <Button
                        key={status}
                        variant={selectedFeedback.status === status ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateStatus(selectedFeedback.id, status)}
                        disabled={updating}
                        data-testid={`status-btn-${status}`}
                      >
                        <config.icon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-muted)] mb-2">Notes</p>
                  <Textarea
                    placeholder="Add internal notes about this feedback..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    data-testid="feedback-notes"
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default FeedbackManagementPage;
